import authStore from "@/src/store/authStore";
import client from "@/src/server/clients/tri-company-client";
import partnerFlowClient from "@/src/server/clients/tri-partner-flow-client";
import { Badge } from "@/src/store/sidebar-types";
import { hasModuleAccess } from "@/src/util/permission";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type { Badge };

const CONSOLE_PERMISSION_KEY = "CargoGo Console";
const ADMIN_PERMISSION_KEY = "CargoGo Admin";
const PARTNER_APPLICATIONS_BADGE_KEY = "partner_applications";

const hasConsoleAccess = (): boolean => {
  const { user, hydrated } = authStore.getState();
  if (!hydrated || !user) return false;
  if (user.isSuperAdmin) return true;
  return hasModuleAccess(user.modules, CONSOLE_PERMISSION_KEY);
};

const hasAdminAccess = (): boolean => {
  const { user, hydrated } = authStore.getState();
  if (!hydrated || !user) return false;
  if (user.isSuperAdmin) return true;
  return hasModuleAccess(user.modules, ADMIN_PERMISSION_KEY);
};

const mergeBadges = (current: Badge[], incoming: Badge[]): Badge[] => {
  const map = new Map(current.map((b) => [b.key, b]));
  for (const badge of incoming) {
    map.set(badge.key, badge);
  }
  return Array.from(map.values());
};

interface SidebarState {
  show: boolean;
  unfoldable: boolean;
  isHovered: boolean;
  toggle: () => void;
  setShow: (visible: boolean) => void;
  toggleUnfoldable: () => void;
  setHover: (visible: boolean) => void;
  badges?: Badge[];
  fetch: () => Promise<void>;
}

let fetchPromise: Promise<void> | null = null;

export const store = create<SidebarState>()(
  persist(
    (set, get) => {
      const fetchConsoleBadge = async (): Promise<Badge | null> => {
        try {
          const result = await client.getPendingCompanies({
            q: "",
            cargogoService: "",
            pageable: { page: 1, size: 9999 },
          });
          if (result?.success && result.errorCode === 0) {
            return {
              key: "new_request",
              count: result.data?.companies?.length ?? 0,
            };
          }
        } catch (err) {
          console.warn("[sidebarStore] getPendingCompanies skipped or failed", err);
        }
        return null;
      };

      const fetchPartnerApplicationsBadge = async (): Promise<Badge | null> => {
        try {
          const result = await partnerFlowClient.getReceivedApplications({
            q: "",
            status: "Pending",
            pageable: { page: 1, size: 1 },
          });
          if (result?.success && result.errorCode === 0) {
            return {
              key: PARTNER_APPLICATIONS_BADGE_KEY,
              count: Number(result.data?.count ?? 0),
            };
          }
        } catch (err) {
          console.warn("[sidebarStore] getReceivedApplications skipped or failed", err);
        }
        return null;
      };

      const fetch = async () => {
        const tasks: Promise<Badge | null>[] = [];
        if (hasConsoleAccess()) {
          tasks.push(fetchConsoleBadge());
        }
        if (hasAdminAccess()) {
          tasks.push(fetchPartnerApplicationsBadge());
        }
        if (tasks.length === 0) {
          return;
        }

        if (fetchPromise) {
          return fetchPromise;
        }

        fetchPromise = (async () => {
          try {
            const results = await Promise.all(tasks);
            let badges = get().badges ?? [];

            if (!hasAdminAccess()) {
              badges = badges.filter((b) => b.key !== PARTNER_APPLICATIONS_BADGE_KEY);
            }
            if (!hasConsoleAccess()) {
              badges = badges.filter((b) => b.key !== "new_request");
            }

            const incoming = results.filter((badge): badge is Badge => badge !== null);
            set({ badges: mergeBadges(badges, incoming) });
          } finally {
            fetchPromise = null;
          }
        })();

        return fetchPromise;
      };

      return {
        show: true,
        unfoldable: true,
        isHovered: false,
        toggle: () => set((state) => ({ show: !state.show })),
        setShow: (val) => set({ show: val }),
        toggleUnfoldable: () => set((state) => ({ unfoldable: !state.unfoldable })),
        setHover: (val) => set({ isHovered: val }),
        badges: [],
        fetch,
      };
    },
    {
      name: "sidebar-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export default store;
